import { useEffect, useState, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getProjects, getCategories, getSubCategories, getRegions, getCities, getDistricts, createProject, updateProject, deleteProject, deleteAllProjects, fixProjectCategories, seedDemoData, uploadProjectImage, getImageUrl, aiImproveDescription, aiGenerateShortDesc, aiGenerateTags, extractCoordinates, uploadPdf, getFileUrl } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, Search, ChevronLeft, ChevronRight, Upload, X, Sparkles, Star, ImageIcon, Tag, MapPin, Package, Store, Globe, Instagram, MessageCircle, Phone, ShoppingBag, FileText, Camera, Award } from 'lucide-react';
import { toast } from 'sonner';
import { getProjectProducts, createProjectProduct, deleteProjectProduct, getProjectSalesChannels, createProjectSalesChannel, deleteProjectSalesChannel, convertProjectToStory } from '../lib/api';
import { useNavigate } from 'react-router-dom';

const CHANNEL_TYPES = [
  { value: 'store', label: 'متجر', icon: Store },
  { value: 'website', label: 'موقع إلكتروني', icon: Globe },
  { value: 'instagram', label: 'انستجرام', icon: Instagram },
  { value: 'whatsapp', label: 'واتساب', icon: MessageCircle },
  { value: 'phone', label: 'هاتف', icon: Phone },
  { value: 'marketplace', label: 'سوق إلكتروني', icon: ShoppingBag },
];

const EMPTY_FORM = {
  project_name_ar: '', project_name_en: '',
  description_ar: '', short_description_ar: '',
  category_id: '', subcategory_id: '',
  region_id: '', city_id: '', district_id: '',
  latitude: '', longitude: '',
  icon_name: '', icon_code: '',
  status: 'draft', review_status: 'pending',
  is_sample: false, is_featured: false,
  data_source: '', notes: '',
  tags: [], images: [], thumbnail: '',
  // New fields
  project_logo: '',
  online_store_url: '',
  sales_location_lat: '', sales_location_lng: '', sales_location_text: '',
  snapchat_url: '', instagram_url: '', mobile_number: '',
  project_profile_pdf: '',
};

export default function ProjectsPage() {
  const { t, language, isRTL } = useLanguage();
  const [projects, setProjects] = useState({ items: [], total: 0 });
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSubCategory, setFilterSubCategory] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deletingProject, setDeletingProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 20;
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState({});
  const [tagInput, setTagInput] = useState('');
  const [mapsLink, setMapsLink] = useState('');
  const [extracting, setExtracting] = useState(false);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);

  // Products & Sales Channels state
  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ name_ar: '', description_ar: '', price: '', image: '' });
  const [productUploading, setProductUploading] = useState(false);
  const productImageRef = useRef(null);
  const [salesChannels, setSalesChannels] = useState([]);
  const [newChannel, setNewChannel] = useState({ channel_type: 'store', label: '', url: '' });

  // Story conversion state
  const navigate = useNavigate();
  const [storyMap, setStoryMap] = useState({});
  const [convertingId, setConvertingId] = useState(null);

  const filteredSubcategories = subcategories.filter(sc => sc.category_id === formData.category_id);
  const filteredCities = cities.filter(c => c.region_id === formData.region_id);
  const filteredDistricts = districts.filter(d => d.city_id === formData.city_id);

  const fetchData = async () => {
    try {
      const [catsRes, subcatsRes, regionsRes, citiesRes, districtsRes] = await Promise.all([
        getCategories(), getSubCategories(), getRegions(), getCities(), getDistricts()
      ]);
      setCategories(catsRes.data);
      setSubcategories(subcatsRes.data);
      setRegions(regionsRes.data);
      setCities(citiesRes.data);
      setDistricts(districtsRes.data);
    } catch (error) {
      toast.error(t('error.general'));
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = { skip: page * pageSize, limit: pageSize };
      if (filterRegion !== 'all') params.region_id = filterRegion;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterCategory !== 'all') params.category_id = filterCategory;
      if (filterSubCategory !== 'all') params.subcategory_id = filterSubCategory;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const response = await getProjects(params);
      setProjects(response.data);
      // Check which projects have stories
      const items = response.data.items || [];
      const { getSuccessStories } = await import('../lib/api');
      const storiesRes = await getSuccessStories({ limit: 1000 });
      const map = {};
      for (const s of (storiesRes.data.items || [])) {
        if (s.related_project_id) map[s.related_project_id] = s.id;
      }
      setStoryMap(map);
    } catch (error) {
      toast.error(t('error.general'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { fetchProjects(); }, [page, filterRegion, filterStatus, filterCategory, filterSubCategory, searchTerm]);

  const handleConvertToStory = async (project) => {
    setConvertingId(project.id);
    try {
      const res = await convertProjectToStory(project.id);
      const data = res.data;
      if (data.already_exists) {
        toast.info(language === 'ar' ? 'يوجد قصة نجاح مرتبطة بالفعل' : 'Story already exists');
        navigate(`/admin/success-stories?edit=${data.story_id}`);
      } else {
        toast.success(language === 'ar' ? 'تم إنشاء قصة النجاح — أكمل البيانات' : 'Story created — complete the data');
        navigate(`/admin/success-stories?edit=${data.story_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
    setConvertingId(null);
  };

  const getRegionName = (id) => { const r = regions.find(x => x.id === id); return language === 'ar' ? r?.name_ar : (r?.name_en || r?.name_ar); };
  const getCategoryName = (id) => { const c = categories.find(x => x.id === id); return language === 'ar' ? c?.name_ar : (c?.name_en || c?.name_ar); };

  const handleOpenDialog = async (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        project_name_ar: project.project_name_ar || '',
        project_name_en: project.project_name_en || '',
        description_ar: project.description_ar || '',
        short_description_ar: project.short_description_ar || '',
        category_id: project.category_id || '',
        subcategory_id: project.subcategory_id || '',
        region_id: project.region_id || '',
        city_id: project.city_id || '',
        district_id: project.district_id || '',
        latitude: project.latitude || '',
        longitude: project.longitude || '',
        icon_name: project.icon_name || '',
        icon_code: project.icon_code || '',
        status: project.status || 'draft',
        review_status: project.review_status || 'pending',
        is_sample: project.is_sample || false,
        is_featured: project.is_featured || false,
        data_source: project.data_source || '',
        notes: project.notes || '',
        tags: project.tags || [],
        images: project.images || [],
        thumbnail: project.thumbnail || '',
        project_logo: project.project_logo || '',
        online_store_url: project.online_store_url || '',
        sales_location_lat: project.sales_location_lat || '',
        sales_location_lng: project.sales_location_lng || '',
        sales_location_text: project.sales_location_text || '',
        snapchat_url: project.snapchat_url || '',
        instagram_url: project.instagram_url || '',
        mobile_number: project.mobile_number || '',
        project_profile_pdf: project.project_profile_pdf || '',
      });
      // Load related products and sales channels
      try {
        const [prodsRes, chansRes] = await Promise.all([
          getProjectProducts(project.id),
          getProjectSalesChannels(project.id),
        ]);
        setProducts(prodsRes.data || []);
        setSalesChannels(chansRes.data || []);
      } catch { setProducts([]); setSalesChannels([]); }
    } else {
      setEditingProject(null);
      setFormData({ ...EMPTY_FORM, tags: [], images: [] });
      setProducts([]);
      setSalesChannels([]);
    }
    setTagInput('');
    setNewProduct({ name_ar: '', description_ar: '', price: '', image: '' });
    setNewChannel({ channel_type: 'store', label: '', url: '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.project_name_ar.trim() || !formData.category_id || !formData.subcategory_id ||
        !formData.region_id || !formData.city_id || !formData.latitude || !formData.longitude) {
      toast.error(t('validation.required'));
      return;
    }
    if (formData.mobile_number && !/^05\d{8}$/.test(formData.mobile_number)) {
      toast.error('رقم الجوال يجب أن يبدأ بـ 05 ويتكون من 10 أرقام');
      return;
    }
    const submitData = {
      ...formData,
      latitude: parseFloat(formData.latitude),
      longitude: parseFloat(formData.longitude),
      district_id: formData.district_id || null,
      sales_location_lat: formData.sales_location_lat ? parseFloat(formData.sales_location_lat) : null,
      sales_location_lng: formData.sales_location_lng ? parseFloat(formData.sales_location_lng) : null,
      mobile_number: formData.mobile_number || null,
      project_logo: formData.project_logo || null,
      online_store_url: formData.online_store_url || null,
      sales_location_text: formData.sales_location_text || null,
      snapchat_url: formData.snapchat_url || null,
      instagram_url: formData.instagram_url || null,
      project_profile_pdf: formData.project_profile_pdf || null,
    };
    setSaving(true);
    try {
      let projectId;
      if (editingProject) {
        await updateProject(editingProject.id, submitData);
        projectId = editingProject.id;
        toast.success(t('success.updated'));
      } else {
        const res = await createProject(submitData);
        projectId = res.data?.id;
        toast.success(t('success.created'));
      }

      // Save new products (local-only items without id)
      if (projectId) {
        for (const prod of products) {
          if (!prod.id) {
            await createProjectProduct(projectId, prod);
          }
        }
        for (const ch of salesChannels) {
          if (!ch.id) {
            await createProjectSalesChannel(projectId, ch);
          }
        }
      }

      setDialogOpen(false);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deletingProject) return;
    try {
      await deleteProject(deletingProject.id);
      toast.success(t('success.deleted'));
      setDeleteDialogOpen(false);
      setDeletingProject(null);
      fetchProjects();
    } catch (error) { toast.error(error.response?.data?.detail || t('error.general')); }
  };

  // Image upload
  const handleImageUpload = useCallback(async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const res = await uploadProjectImage(file);
        const { image_path, thumbnail_path } = res.data;
        setFormData(prev => ({
          ...prev,
          images: [...(prev.images || []), image_path],
          thumbnail: prev.thumbnail || thumbnail_path,
        }));
      }
      toast.success('تم رفع الصور بنجاح');
    } catch (err) {
      toast.error('فشل رفع الصورة');
    } finally { setUploading(false); }
  }, []);

  const removeImage = (idx) => {
    setFormData(prev => {
      const newImages = [...prev.images];
      newImages.splice(idx, 1);
      return { ...prev, images: newImages, thumbnail: newImages.length ? prev.thumbnail : '' };
    });
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    handleImageUpload(e.dataTransfer.files);
  }, [handleImageUpload]);

  // AI functions
  const handleAiImprove = async () => {
    if (!formData.description_ar?.trim()) { toast.error('أدخل الوصف أولاً'); return; }
    setAiLoading(p => ({ ...p, improve: true }));
    try {
      const res = await aiImproveDescription(formData.description_ar);
      setFormData(prev => ({ ...prev, description_ar: res.data.result }));
      toast.success('تم تحسين الوصف');
    } catch { toast.error('فشل تحسين الوصف'); }
    finally { setAiLoading(p => ({ ...p, improve: false })); }
  };

  const handleAiShortDesc = async () => {
    setAiLoading(p => ({ ...p, short: true }));
    try {
      const res = await aiGenerateShortDesc(formData.project_name_ar, formData.description_ar);
      setFormData(prev => ({ ...prev, short_description_ar: res.data.result }));
      toast.success('تم توليد الوصف المختصر');
    } catch { toast.error('فشل التوليد'); }
    finally { setAiLoading(p => ({ ...p, short: false })); }
  };

  const handleAiTags = async () => {
    setAiLoading(p => ({ ...p, tags: true }));
    try {
      const catName = getCategoryName(formData.category_id) || '';
      const res = await aiGenerateTags(formData.project_name_ar, catName, formData.description_ar);
      setFormData(prev => ({ ...prev, tags: res.data.result }));
      toast.success('تم توليد كلمات مفتاحية');
    } catch { toast.error('فشل التوليد'); }
    finally { setAiLoading(p => ({ ...p, tags: false })); }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (idx) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter((_, i) => i !== idx) }));
  };

  const handleExtractCoords = async () => {
    if (!mapsLink.trim()) { toast.error('الصق رابط Google Maps أولاً'); return; }
    setExtracting(true);
    try {
      const res = await extractCoordinates(mapsLink.trim());
      const { latitude, longitude } = res.data;
      setFormData(prev => ({ ...prev, latitude: String(latitude), longitude: String(longitude) }));
      toast.success(`تم استخراج الإحداثيات: ${latitude}, ${longitude}`);
      setMapsLink('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل استخراج الإحداثيات من الرابط');
    } finally { setExtracting(false); }
  };

  const filteredProjects = projects.items || [];

  const totalPages = Math.ceil(projects.total / pageSize);

  if (loading && categories.length === 0) {
    return (<div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#0A4C3A]" /></div>);
  }

  return (
    <div className="space-y-4" data-testid="projects-page">
      {/* Header — Row 1: Search + Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative w-56">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder={t('search')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="ps-10" data-testid="projects-search" />
        </div>
        <Select value={filterRegion} onValueChange={(v) => { setFilterRegion(v); setPage(0); }}>
          <SelectTrigger className="w-40" data-testid="projects-filter-region"><SelectValue placeholder={t('region')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            {regions.map(r => <SelectItem key={r.id} value={r.id}>{language === 'ar' ? r.name_ar : (r.name_en || r.name_ar)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(0); }}>
          <SelectTrigger className="w-32" data-testid="projects-filter-status"><SelectValue placeholder={t('status')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            <SelectItem value="draft">{t('draft')}</SelectItem>
            <SelectItem value="published">{t('published')}</SelectItem>
            <SelectItem value="archived">{t('archived')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={(v) => { setFilterCategory(v); setFilterSubCategory('all'); setPage(0); }}>
          <SelectTrigger className="w-40" data-testid="projects-filter-category"><SelectValue placeholder={language === 'ar' ? 'التصنيف' : 'Category'} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{language === 'ar' ? c.name_ar : (c.name_en || c.name_ar)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterSubCategory} onValueChange={(v) => { setFilterSubCategory(v); setPage(0); }}>
          <SelectTrigger className="w-40" data-testid="projects-filter-subcategory"><SelectValue placeholder={language === 'ar' ? 'التصنيف الفرعي' : 'Subcategory'} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            {subcategories.filter(s => filterCategory === 'all' || s.category_id === filterCategory).map(s => <SelectItem key={s.id} value={s.id}>{language === 'ar' ? s.name_ar : (s.name_en || s.name_ar)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Header — Row 2: Action Buttons */}
      <div className="flex flex-wrap gap-2 items-center">
        <Button onClick={() => handleOpenDialog()} className="bg-[#0A4C3A] hover:bg-[#073B2D]" data-testid="add-project-button">
          <Plus className="w-4 h-4 me-2" />{t('add_project')}
        </Button>
        <Button
          variant="outline"
          data-testid="seed-demo-btn"
          onClick={async () => {
            if (!window.confirm(language === 'ar' ? 'سيتم حذف المشاريع الحالية وتوليد 10,000 مشروع جديد مع 300 مميز. متأكد؟' : 'Delete existing and generate 10,000 projects?')) return;
            try {
              const { data } = await seedDemoData();
              if (data.skipped) { toast.info(data.message); return; }
              toast.success(language === 'ar' ? `تم توليد ${data.total_projects} مشروع + ${data.featured} مميز` : `Added ${data.total_projects} projects`);
              fetchProjects();
            } catch { toast.error(language === 'ar' ? 'فشل' : 'Failed'); }
          }}
        >
          {language === 'ar' ? 'بيانات تجريبية' : 'Seed Demo'}
        </Button>
        <Button
          variant="outline"
          data-testid="fix-categories-btn"
          onClick={async () => {
            try {
              const { data } = await fixProjectCategories();
              toast.success(language === 'ar' ? `تم ربط ${data.updated} مشروع بالتصنيفات (${data.remaining_empty} بدون تصنيف)` : `Fixed ${data.updated} projects (${data.remaining_empty} remaining)`);
              fetchProjects();
            } catch { toast.error(language === 'ar' ? 'فشل الربط' : 'Fix failed'); }
          }}
        >
          {language === 'ar' ? 'ربط التصنيفات' : 'Fix Categories'}
        </Button>
        <Button
          variant="destructive"
          data-testid="delete-all-projects-btn"
          onClick={async () => {
            if (!window.confirm(language === 'ar' ? 'هل تريد مسح جميع المشاريع؟ لا يمكن التراجع!' : 'Delete ALL projects? This cannot be undone!')) return;
            try {
              const { data } = await deleteAllProjects();
              toast.success(language === 'ar' ? `تم مسح ${data.deleted_projects} مشروع` : `Deleted ${data.deleted_projects} projects`);
              fetchData();
            } catch { toast.error(language === 'ar' ? 'فشل المسح' : 'Delete failed'); }
          }}
        >
          <Trash2 className="w-4 h-4 me-2" />{language === 'ar' ? 'مسح المشاريع' : 'Delete All'}
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-white border border-gray-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-medium">{t('project_name_ar')}</TableHead>
                  <TableHead className="font-medium">{t('category')}</TableHead>
                  <TableHead className="font-medium">{t('region')}</TableHead>
                  <TableHead className="font-medium">{t('status')}</TableHead>
                  <TableHead className="font-medium">مميز</TableHead>
                  <TableHead className="font-medium text-center">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">{loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : t('no_data')}</TableCell></TableRow>
                ) : filteredProjects.map(project => (
                  <TableRow key={project.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {project.is_featured && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
                        {project.project_name_ar}
                      </div>
                    </TableCell>
                    <TableCell>{getCategoryName(project.category_id)}</TableCell>
                    <TableCell>{getRegionName(project.region_id)}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${project.status === 'published' ? 'bg-green-100 text-green-800' : project.status === 'archived' ? 'bg-gray-100 text-gray-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {t(project.status)}
                      </span>
                    </TableCell>
                    <TableCell>{project.is_featured ? <Star className="w-4 h-4 text-amber-500 fill-amber-500" /> : '—'}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {storyMap[project.id] ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-bold" data-testid={`story-badge-${project.id}`}>
                            <Award className="w-3 h-3" /> {language === 'ar' ? 'قصة نجاح' : 'Story'}
                          </span>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => handleConvertToStory(project)}
                            disabled={convertingId === project.id}
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            data-testid={`convert-story-${project.id}`}
                            title={language === 'ar' ? 'تحويل إلى قصة نجاح' : 'Convert to Story'}>
                            {convertingId === project.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(project)} data-testid={`edit-project-${project.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => { setDeletingProject(project); setDeleteDialogOpen(true); }} className="text-red-600 hover:text-red-700" data-testid={`delete-project-${project.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <span className="text-sm text-gray-600">{t('total_rows')}: {projects.total}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                  {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                </Button>
                <span className="text-sm text-gray-600">{page + 1} / {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                  {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProject ? t('edit_project') : t('add_project')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">

            {/* Names */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('project_name_ar')} *</Label>
                <Input value={formData.project_name_ar} onChange={(e) => setFormData({ ...formData, project_name_ar: e.target.value })} data-testid="project-name-ar" />
              </div>
              <div className="space-y-2">
                <Label>{t('project_name_en')}</Label>
                <Input value={formData.project_name_en} onChange={(e) => setFormData({ ...formData, project_name_en: e.target.value })} dir="ltr" data-testid="project-name-en" />
              </div>
            </div>

            {/* Description + AI */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>الوصف (عربي)</Label>
                <Button variant="outline" size="sm" onClick={handleAiImprove} disabled={aiLoading.improve} className="gap-1.5 text-xs h-7" data-testid="ai-improve-btn">
                  {aiLoading.improve ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  تحسين الوصف
                </Button>
              </div>
              <Textarea value={formData.description_ar} onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })} rows={4} placeholder="وصف المشروع..." data-testid="project-description" />
            </div>

            {/* Short Description + AI */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>وصف مختصر</Label>
                <Button variant="outline" size="sm" onClick={handleAiShortDesc} disabled={aiLoading.short} className="gap-1.5 text-xs h-7" data-testid="ai-short-desc-btn">
                  {aiLoading.short ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  توليد مختصر
                </Button>
              </div>
              <Textarea value={formData.short_description_ar} onChange={(e) => setFormData({ ...formData, short_description_ar: e.target.value })} rows={2} placeholder="سطر أو سطرين..." data-testid="project-short-description" />
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><ImageIcon className="w-4 h-4" /> صور المشروع</Label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#0A4C3A] transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                data-testid="image-dropzone"
              >
                <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleImageUpload(e.target.files)} />
                {uploading ? (
                  <div className="flex items-center justify-center gap-2 py-2"><Loader2 className="w-5 h-5 animate-spin text-[#0A4C3A]" /><span className="text-sm text-gray-500">جاري الرفع والمعالجة...</span></div>
                ) : (
                  <div className="flex flex-col items-center gap-1 py-2">
                    <Upload className="w-6 h-6 text-gray-400" />
                    <span className="text-sm text-gray-500">اضغط أو اسحب الصور هنا</span>
                    <span className="text-xs text-gray-400">يتم ضغط الصور وتحويلها تلقائياً</span>
                  </div>
                )}
              </div>
              {/* Image preview */}
              {formData.images?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.images.map((img, idx) => (
                    <div key={idx} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                      <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                      <button className="absolute top-0.5 end-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => removeImage(idx)} data-testid={`remove-image-${idx}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ══════════ Project Logo ══════════ */}
            <div className="space-y-2 border-t border-gray-200 pt-5">
              <Label className="flex items-center gap-2"><Camera className="w-4 h-4 text-[#0A4C3A]" /> لوجو المشروع</Label>
              <div className="flex items-center gap-3">
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  if (!e.target.files?.length) return;
                  setLogoUploading(true);
                  try {
                    const res = await uploadProjectImage(e.target.files[0]);
                    setFormData(prev => ({ ...prev, project_logo: res.data.image_path }));
                    toast.success('تم رفع اللوجو');
                  } catch { toast.error('فشل رفع اللوجو'); }
                  finally { setLogoUploading(false); }
                }} />
                {formData.project_logo ? (
                  <div className="relative group">
                    <img src={getImageUrl(formData.project_logo)} alt="Logo" className="w-20 h-20 rounded-xl object-cover border-2 border-gray-200" />
                    <button className="absolute -top-1.5 -end-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setFormData(prev => ({ ...prev, project_logo: '' }))} data-testid="remove-logo">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" className="gap-2 h-20 w-20 flex-col text-xs" onClick={() => logoInputRef.current?.click()} disabled={logoUploading} data-testid="upload-logo-btn">
                    {logoUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 text-gray-400" />}
                    <span className="text-gray-400">رفع لوجو</span>
                  </Button>
                )}
                <p className="text-xs text-gray-400">إذا لم يتم رفع لوجو، سيظهر لوجو النشاط تلقائياً</p>
              </div>
            </div>

            {/* ══════════ Sales Channels (Direct Fields) ══════════ */}
            <div className="space-y-4 border-t border-gray-200 pt-5">
              <Label className="flex items-center gap-2 text-base font-bold">
                <Store className="w-5 h-5 text-[#0A4C3A]" /> قنوات البيع
              </Label>
              
              {/* Online Store */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5 text-sm"><Globe className="w-3.5 h-3.5" /> رابط المتجر الإلكتروني</Label>
                <Input value={formData.online_store_url} onChange={(e) => setFormData({ ...formData, online_store_url: e.target.value })} placeholder="https://..." dir="ltr" data-testid="online-store-url" />
              </div>

              {/* Sales Location */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5 text-sm"><MapPin className="w-3.5 h-3.5 text-red-500" /> موقع البيع</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">خط العرض</Label>
                    <Input type="number" step="any" value={formData.sales_location_lat} onChange={(e) => setFormData({ ...formData, sales_location_lat: e.target.value })} dir="ltr" placeholder="24.7136" className="h-9 text-sm" data-testid="sales-lat" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">خط الطول</Label>
                    <Input type="number" step="any" value={formData.sales_location_lng} onChange={(e) => setFormData({ ...formData, sales_location_lng: e.target.value })} dir="ltr" placeholder="46.6753" className="h-9 text-sm" data-testid="sales-lng" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">العنوان (اختياري)</Label>
                    <Input value={formData.sales_location_text} onChange={(e) => setFormData({ ...formData, sales_location_text: e.target.value })} placeholder="العنوان..." className="h-9 text-sm" data-testid="sales-address" />
                  </div>
                </div>
              </div>

              {/* Social & Contact */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5 text-sm"><svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor"><path d="M12.206.793c.99 0 4.347.276 5.93 3.521.529 1.097.403 2.578.371 3.157l-.004.045c-.081 1.033-.262 2.162-.573 3.27-.375 1.336-.892 2.555-1.526 3.52a9.41 9.41 0 0 1-1.396 1.673c-.735.686-1.622 1.267-2.638 1.267-.658 0-1.117-.168-1.452-.335a3.5 3.5 0 0 1-.559-.321c-.364-.254-.756-.431-1.184-.454a2.91 2.91 0 0 0-1.191.397 4.58 4.58 0 0 1-.552.262c-.31.11-.696.195-1.164.195-1.019 0-1.907-.605-2.643-1.293a9.8 9.8 0 0 1-1.392-1.68c-.633-.968-1.148-2.19-1.523-3.53a14.44 14.44 0 0 1-.573-3.266l-.004-.045c-.032-.58-.158-2.06.371-3.157C2.248 1.07 5.604.793 6.595.793c.486 0 1.634.163 2.774.98.458.328.78.647.981.853.201-.206.523-.525.981-.853C12.47.956 13.619.793 14.205.793Z"/></svg> Snapchat</Label>
                  <Input value={formData.snapchat_url} onChange={(e) => setFormData({ ...formData, snapchat_url: e.target.value })} placeholder="https://snapchat.com/..." dir="ltr" className="text-sm" data-testid="snapchat-url" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5 text-sm"><Instagram className="w-3.5 h-3.5" /> Instagram</Label>
                  <Input value={formData.instagram_url} onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })} placeholder="https://instagram.com/..." dir="ltr" className="text-sm" data-testid="instagram-url" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5 text-sm"><Phone className="w-3.5 h-3.5" /> رقم الجوال</Label>
                  <Input value={formData.mobile_number} onChange={(e) => {
                    const val = e.target.value.replace(/[^\d]/g, '').slice(0, 10);
                    setFormData({ ...formData, mobile_number: val });
                  }} placeholder="05XXXXXXXX" dir="ltr" className="text-sm" maxLength={10} data-testid="mobile-number" />
                  {formData.mobile_number && !/^05\d{8}$/.test(formData.mobile_number) && (
                    <p className="text-xs text-red-500">يجب أن يبدأ بـ 05 ويتكون من 10 أرقام</p>
                  )}
                </div>
              </div>
            </div>

            {/* ══════════ PDF Upload ══════════ */}
            <div className="space-y-2 border-t border-gray-200 pt-5">
              <Label className="flex items-center gap-2"><FileText className="w-4 h-4 text-[#0A4C3A]" /> ملف تعريفي (PDF)</Label>
              <div className="flex items-center gap-3">
                <input ref={pdfInputRef} type="file" accept="application/pdf" className="hidden" onChange={async (e) => {
                  if (!e.target.files?.length) return;
                  const file = e.target.files[0];
                  if (file.type !== 'application/pdf') { toast.error('يجب أن يكون الملف بصيغة PDF'); return; }
                  if (file.size > 3 * 1024 * 1024) { toast.error('حجم الملف يجب ألا يتجاوز 3 ميجابايت'); return; }
                  setPdfUploading(true);
                  try {
                    const res = await uploadPdf(file);
                    setFormData(prev => ({ ...prev, project_profile_pdf: res.data.pdf_path }));
                    toast.success('تم رفع الملف');
                  } catch (err) { toast.error(err.response?.data?.detail || 'فشل رفع الملف'); }
                  finally { setPdfUploading(false); }
                }} />
                {formData.project_profile_pdf ? (
                  <div className="flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-lg">
                    <FileText className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-700 font-medium">تم رفع الملف</span>
                    <button className="ms-2 text-red-500 hover:text-red-700" onClick={() => setFormData(prev => ({ ...prev, project_profile_pdf: '' }))} data-testid="remove-pdf">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" className="gap-2" onClick={() => pdfInputRef.current?.click()} disabled={pdfUploading} data-testid="upload-pdf-btn">
                    {pdfUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    رفع ملف PDF
                  </Button>
                )}
                <p className="text-xs text-gray-400">PDF فقط، الحد الأقصى 3MB</p>
              </div>
            </div>

            {/* Category & Subcategory */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('category')} *</Label>
                <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v, subcategory_id: '' })}>
                  <SelectTrigger data-testid="project-category-select"><SelectValue placeholder={t('select_category')} /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{language === 'ar' ? c.name_ar : (c.name_en || c.name_ar)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('subcategory')} *</Label>
                <Select value={formData.subcategory_id} onValueChange={(v) => setFormData({ ...formData, subcategory_id: v })} disabled={!formData.category_id}>
                  <SelectTrigger data-testid="project-subcategory-select"><SelectValue placeholder={t('select_subcategory')} /></SelectTrigger>
                  <SelectContent>{filteredSubcategories.map(s => <SelectItem key={s.id} value={s.id}>{language === 'ar' ? s.name_ar : (s.name_en || s.name_ar)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Region / City / District */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('region')} *</Label>
                <Select value={formData.region_id} onValueChange={(v) => setFormData({ ...formData, region_id: v, city_id: '', district_id: '' })}>
                  <SelectTrigger data-testid="project-region-select"><SelectValue placeholder={t('select_region')} /></SelectTrigger>
                  <SelectContent>{regions.map(r => <SelectItem key={r.id} value={r.id}>{language === 'ar' ? r.name_ar : (r.name_en || r.name_ar)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('city')} *</Label>
                <Select value={formData.city_id} onValueChange={(v) => setFormData({ ...formData, city_id: v, district_id: '' })} disabled={!formData.region_id}>
                  <SelectTrigger data-testid="project-city-select"><SelectValue placeholder={t('select_city')} /></SelectTrigger>
                  <SelectContent>{filteredCities.map(c => <SelectItem key={c.id} value={c.id}>{language === 'ar' ? c.name_ar : (c.name_en || c.name_ar)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('district')}</Label>
                <Select value={formData.district_id} onValueChange={(v) => setFormData({ ...formData, district_id: v })} disabled={!formData.city_id}>
                  <SelectTrigger data-testid="project-district-select"><SelectValue placeholder={t('select_district')} /></SelectTrigger>
                  <SelectContent>{filteredDistricts.map(d => <SelectItem key={d.id} value={d.id}>{language === 'ar' ? d.name_ar : (d.name_en || d.name_ar)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Google Maps Link → Coordinates */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-red-500" /> استخراج الإحداثيات من Google Maps</Label>
              <div className="flex gap-2">
                <Input
                  value={mapsLink}
                  onChange={(e) => setMapsLink(e.target.value)}
                  placeholder="الصق رابط Google Maps هنا..."
                  dir="ltr"
                  className="flex-1 text-sm"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleExtractCoords(); } }}
                  data-testid="maps-link-input"
                />
                <Button variant="outline" onClick={handleExtractCoords} disabled={extracting} className="gap-1.5 shrink-0" data-testid="extract-coords-btn">
                  {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                  استخراج
                </Button>
              </div>
              <p className="text-xs text-gray-400">يدعم الروابط المختصرة (maps.app.goo.gl) والعادية</p>
            </div>

            {/* Map Pin Picker */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-emerald-600" /> اختر الموقع على الخريطة</Label>
              <div
                className="relative w-full h-[280px] rounded-xl border border-gray-200 overflow-hidden bg-gray-100"
                data-testid="map-pin-picker"
              >
                <iframe
                  key={`${formData.latitude || 24.7136}-${formData.longitude || 46.6753}`}
                  title="map-picker"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${(parseFloat(formData.longitude || 46.6753) - 0.02).toFixed(4)}%2C${(parseFloat(formData.latitude || 24.7136) - 0.015).toFixed(4)}%2C${(parseFloat(formData.longitude || 46.6753) + 0.02).toFixed(4)}%2C${(parseFloat(formData.latitude || 24.7136) + 0.015).toFixed(4)}&layer=mapnik&marker=${formData.latitude || 24.7136}%2C${formData.longitude || 46.6753}`}
                />
                <div
                  className="absolute inset-0 cursor-crosshair"
                  data-testid="map-click-overlay"
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / rect.width;
                    const y = (e.clientY - rect.top) / rect.height;
                    const lng = parseFloat(formData.longitude || 46.6753);
                    const lat = parseFloat(formData.latitude || 24.7136);
                    const span = 0.04;
                    const newLng = (lng - 0.02) + x * span;
                    const newLat = (lat + 0.015) - y * 0.03;
                    setFormData(prev => ({
                      ...prev,
                      latitude: newLat.toFixed(7),
                      longitude: newLng.toFixed(7),
                    }));
                  }}
                />
              </div>
              <p className="text-xs text-gray-400">اضغط على الخريطة لتحديد الموقع أو عدّل الإحداثيات يدوياً</p>
            </div>

            {/* Coordinates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('latitude')} *</Label>
                <Input type="number" step="any" value={formData.latitude} onChange={(e) => setFormData({ ...formData, latitude: e.target.value })} dir="ltr" placeholder="24.7136" data-testid="project-latitude" />
              </div>
              <div className="space-y-2">
                <Label>{t('longitude')} *</Label>
                <Input type="number" step="any" value={formData.longitude} onChange={(e) => setFormData({ ...formData, longitude: e.target.value })} dir="ltr" placeholder="46.6753" data-testid="project-longitude" />
              </div>
            </div>

            {/* Status + Featured */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('project_status')}</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">{t('draft')}</SelectItem>
                    <SelectItem value="published">{t('published')}</SelectItem>
                    <SelectItem value="archived">{t('archived')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('review_status')}</Label>
                <Select value={formData.review_status} onValueChange={(v) => setFormData({ ...formData, review_status: v })}>
                  <SelectTrigger data-testid="project-review-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{t('pending')}</SelectItem>
                    <SelectItem value="approved">{t('approved')}</SelectItem>
                    <SelectItem value="rejected">{t('rejected')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tags + AI */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><Tag className="w-4 h-4" /> الكلمات المفتاحية</Label>
                <Button variant="outline" size="sm" onClick={handleAiTags} disabled={aiLoading.tags} className="gap-1.5 text-xs h-7" data-testid="ai-tags-btn">
                  {aiLoading.tags ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  توليد كلمات مفتاحية
                </Button>
              </div>
              <div className="flex gap-2">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="أضف وسم..." onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} data-testid="tag-input" />
                <Button variant="outline" size="sm" onClick={addTag} data-testid="add-tag-btn">+</Button>
              </div>
              {formData.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {formData.tags.map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0A4C3A]/10 text-[#0A4C3A] rounded-full text-xs font-medium">
                      {tag}
                      <button onClick={() => removeTag(i)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Featured + Sample toggles */}
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-3">
                <Label htmlFor="is_featured" className="flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-amber-500" /> مشروع مميز
                </Label>
                <Switch id="is_featured" checked={formData.is_featured} onCheckedChange={(c) => setFormData({ ...formData, is_featured: c })} data-testid="project-is-featured" />
              </div>
              <div className="flex items-center gap-3">
                <Label htmlFor="is_sample">{t('is_sample')}</Label>
                <Switch id="is_sample" checked={formData.is_sample} onCheckedChange={(c) => setFormData({ ...formData, is_sample: c })} data-testid="project-is-sample" />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>{t('notes')}</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} data-testid="project-notes" />
            </div>

            {/* ══════════ Products Section ══════════ */}
            <div className="space-y-3 border-t border-gray-200 pt-5">
              <Label className="flex items-center gap-2 text-base font-bold">
                <Package className="w-5 h-5 text-[#0A4C3A]" /> المنتجات
              </Label>
              {/* Existing products list */}
              {products.length > 0 && (
                <div className="space-y-2">
                  {products.map((prod, idx) => (
                    <div key={prod.id || `new-${idx}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid={`product-item-${idx}`}>
                      {prod.image && <img src={getImageUrl(prod.image)} alt="" className="w-12 h-12 rounded-lg object-cover border border-gray-200" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{prod.name_ar}</p>
                        {prod.description_ar && <p className="text-xs text-gray-500 truncate">{prod.description_ar}</p>}
                        {prod.price && <span className="text-xs text-amber-600 font-bold">{prod.price} ر.س</span>}
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 shrink-0" onClick={() => {
                        if (prod.id && editingProject) {
                          deleteProjectProduct(editingProject.id, prod.id).then(() => {
                            setProducts(prev => prev.filter(p => p !== prod));
                            toast.success('تم حذف المنتج');
                          }).catch(() => toast.error('فشل حذف المنتج'));
                        } else {
                          setProducts(prev => prev.filter((_, i) => i !== idx));
                        }
                      }} data-testid={`delete-product-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {/* Add new product form */}
              <div className="p-3 bg-[#0A4C3A]/5 rounded-lg border border-dashed border-[#0A4C3A]/20 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">اسم المنتج *</Label>
                    <Input value={newProduct.name_ar} onChange={(e) => setNewProduct(p => ({ ...p, name_ar: e.target.value }))} placeholder="اسم المنتج..." className="h-9 text-sm" data-testid="new-product-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">السعر</Label>
                    <Input type="number" step="any" value={newProduct.price} onChange={(e) => setNewProduct(p => ({ ...p, price: e.target.value }))} placeholder="0.00" dir="ltr" className="h-9 text-sm" data-testid="new-product-price" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">وصف المنتج</Label>
                  <Input value={newProduct.description_ar} onChange={(e) => setNewProduct(p => ({ ...p, description_ar: e.target.value }))} placeholder="وصف مختصر..." className="h-9 text-sm" data-testid="new-product-desc" />
                </div>
                <div className="flex items-center gap-2">
                  <input ref={productImageRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    if (!e.target.files?.length) return;
                    setProductUploading(true);
                    try {
                      const res = await uploadProjectImage(e.target.files[0]);
                      setNewProduct(p => ({ ...p, image: res.data.image_path }));
                      toast.success('تم رفع صورة المنتج');
                    } catch { toast.error('فشل رفع الصورة'); }
                    finally { setProductUploading(false); }
                  }} />
                  <Button type="button" variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => productImageRef.current?.click()} disabled={productUploading} data-testid="upload-product-image">
                    {productUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    صورة المنتج
                  </Button>
                  {newProduct.image && <img src={getImageUrl(newProduct.image)} alt="" className="w-9 h-9 rounded object-cover border" />}
                  <div className="flex-1" />
                  <Button type="button" size="sm" className="bg-[#0A4C3A] hover:bg-[#073B2D] gap-1.5 text-xs" onClick={() => {
                    if (!newProduct.name_ar.trim()) { toast.error('أدخل اسم المنتج'); return; }
                    setProducts(prev => [...prev, { ...newProduct, price: newProduct.price ? parseFloat(newProduct.price) : null }]);
                    setNewProduct({ name_ar: '', description_ar: '', price: '', image: '' });
                    toast.success('تم إضافة المنتج');
                  }} data-testid="add-product-btn">
                    <Plus className="w-3 h-3" /> إضافة منتج
                  </Button>
                </div>
              </div>
            </div>

            {/* ══════════ Sales Channels Section ══════════ */}
            <div className="space-y-3 border-t border-gray-200 pt-5">
              <Label className="flex items-center gap-2 text-base font-bold">
                <Store className="w-5 h-5 text-[#0A4C3A]" /> قنوات البيع
              </Label>
              {/* Existing channels list */}
              {salesChannels.length > 0 && (
                <div className="space-y-2">
                  {salesChannels.map((ch, idx) => {
                    const chType = CHANNEL_TYPES.find(ct => ct.value === ch.channel_type);
                    const ChIcon = chType?.icon || Globe;
                    return (
                      <div key={ch.id || `new-${idx}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid={`channel-item-${idx}`}>
                        <div className="w-9 h-9 rounded-lg bg-[#0A4C3A]/10 flex items-center justify-center shrink-0">
                          <ChIcon className="w-4 h-4 text-[#0A4C3A]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm">{ch.label || chType?.label || ch.channel_type}</p>
                          {ch.url && <p className="text-xs text-blue-500 truncate" dir="ltr">{ch.url}</p>}
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 shrink-0" onClick={() => {
                          if (ch.id && editingProject) {
                            deleteProjectSalesChannel(editingProject.id, ch.id).then(() => {
                              setSalesChannels(prev => prev.filter(c => c !== ch));
                              toast.success('تم حذف القناة');
                            }).catch(() => toast.error('فشل حذف القناة'));
                          } else {
                            setSalesChannels(prev => prev.filter((_, i) => i !== idx));
                          }
                        }} data-testid={`delete-channel-${idx}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Add new channel form */}
              <div className="p-3 bg-[#0A4C3A]/5 rounded-lg border border-dashed border-[#0A4C3A]/20 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">نوع القناة *</Label>
                    <Select value={newChannel.channel_type} onValueChange={(v) => setNewChannel(c => ({ ...c, channel_type: v, label: CHANNEL_TYPES.find(ct => ct.value === v)?.label || '' }))}>
                      <SelectTrigger className="h-9 text-sm" data-testid="new-channel-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CHANNEL_TYPES.map(ct => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">التسمية</Label>
                    <Input value={newChannel.label} onChange={(e) => setNewChannel(c => ({ ...c, label: e.target.value }))} placeholder="اسم مخصص..." className="h-9 text-sm" data-testid="new-channel-label" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">الرابط / الرقم</Label>
                    <Input value={newChannel.url} onChange={(e) => setNewChannel(c => ({ ...c, url: e.target.value }))} placeholder="https://..." dir="ltr" className="h-9 text-sm" data-testid="new-channel-url" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button type="button" size="sm" className="bg-[#0A4C3A] hover:bg-[#073B2D] gap-1.5 text-xs" onClick={() => {
                    if (!newChannel.channel_type) { toast.error('اختر نوع القناة'); return; }
                    const label = newChannel.label || CHANNEL_TYPES.find(ct => ct.value === newChannel.channel_type)?.label || newChannel.channel_type;
                    setSalesChannels(prev => [...prev, { ...newChannel, label }]);
                    setNewChannel({ channel_type: 'store', label: '', url: '' });
                    toast.success('تم إضافة القناة');
                  }} data-testid="add-channel-btn">
                    <Plus className="w-3 h-3" /> إضافة قناة
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0A4C3A] hover:bg-[#073B2D]" data-testid="save-project-button">
              {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle>
            <AlertDialogDescription>{deletingProject?.project_name_ar}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-project">{t('delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
